'use client'

import { useOrganization, useUser } from '@clerk/nextjs'
import { useEffect } from 'react'

import { clearRumIdentity, setRumIdentity } from './browser'

/**
 * Bridges Clerk auth state into Datadog RUM so every session is attributed
 * to a stable user. Mounted once inside each app's `<ClerkProvider>` tree
 * (`app/layout.tsx` in the web app, the platform-admin layout in the admin
 * app).
 *
 * Fire-and-forget — never awaits. While Clerk is still loading, RUM stays in
 * its anonymous state until `useUser()` / `useOrganization()` settle.
 *
 * Its own subpath (`@brew/observability/datadog-user-sync`) rather than a
 * re-export from `./browser`: `browser` is imported by both apps'
 * `instrumentation-client.ts`, which must not pull Clerk's hooks into the
 * pre-hydration chunk.
 */
export function DatadogUserSync() {
  const { user, isLoaded: isUserLoaded } = useUser()
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization()

  useEffect(() => {
    if (!(isUserLoaded && isOrganizationLoaded)) {
      return
    }
    if (!user) {
      clearRumIdentity()
      return
    }
    setRumIdentity({
      user: {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName ?? user.username ?? undefined,
      },
      account: organization
        ? { id: organization.id, name: organization.name }
        : undefined,
    })
  }, [isOrganizationLoaded, isUserLoaded, organization, user])

  return null
}
